import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Switch, useLocation, withRouter } from 'react-router-dom';

import { IntlMessageProvider } from '../intl/IntMessageslProvider';
import OrderConfirmation from '../components/confirmation/OrderConfirmation';
import OrderSummary from '../components/orderSummary/OrderSummary';
import DrumallGiftCardForm from '../drumall/DrumallGiftCardForm';
import { SettingsProvider, useSettings } from '../mall_settings/SettingsProvider';

import { UserProvider, useUsers } from '../user/UserContext';
import AmountCirclePortal from '../user/amountCircle/AmountCirclePortal';
import UserLoginWidgetPortal from '../user/loginWidget/UserLoginWidget';
import EditGiftCardFormForPath from '../components/paths/EditGiftCardFormForPath';

import { appStore } from './appStore';
import { PRODUCT_PATH } from '../components/paths/utils';
import { SegmentProvider, useSegment } from "../segment/SegmentProvider";
import { SessionProvider } from '../session/SessionProvider';
import { segmentInit } from '../segment/segmentInit';
import { isAllCookiesAllowed } from '../segment/segmentUtils';
import {sentryInit} from "../utils/sentry";


const DrumallApp = () => {
    const { phoebe, segmentEnabled, mallCode, orderingToken, sentryDSN } = useSettings();
    const sendEvents = phoebe && segmentEnabled && isAllCookiesAllowed();
    const { pageTrack } = useSegment();
    const { fetching } = useUsers();

    if (sentryDSN) {
        sentryInit(sentryDSN, mallCode, orderingToken);
    }

    if (sendEvents && !window.analytics) {
        segmentInit(phoebe);
    }

    const location = useLocation();
    useEffect(() => {
        if (!fetching) pageTrack(location.pathname)
    }, [location, fetching]);

    return (
        <div className="app">
            <main className="app-wrapper" role="main">
                <Switch>
                    <Route path="/buy/summary" render={props => <OrderSummary {...props} />} />
                    <Route path="/buy/confirmation" component={OrderConfirmation} />
                    <Route path="/buy/product-edit/:lineItemId" component={EditGiftCardFormForPath} />
                    <Route exact path={`${PRODUCT_PATH}:slug`} component={DrumallGiftCardForm} />
                </Switch>
            </main>
        </div>
    );
}

class ScrollToTop extends React.Component {
    componentDidUpdate(prevProps) {
        if (this.props.location !== prevProps.location) window.scrollTo(0, 0);
    }

    render() {
        return this.props.children;
    }
}

ScrollToTop.propTypes = {
    location: PropTypes.object.isRequired,
    children: PropTypes.object.isRequired
};

const ScrollToTopWrap = withRouter(ScrollToTop);

const DrumallReduxApp = ({ initialMessages }) => (
    <SettingsProvider>
        <Provider store={appStore}>
            <IntlMessageProvider locale="en-us" initialMessages={initialMessages}>
                <BrowserRouter>
                    <ScrollToTopWrap>
                        <SessionProvider>
                            <UserProvider>
                                <AmountCirclePortal />
                                <UserLoginWidgetPortal />
                                <SegmentProvider>
                                    <DrumallApp />
                                </SegmentProvider>
                            </UserProvider>
                        </SessionProvider>
                    </ScrollToTopWrap>
                </BrowserRouter>
            </IntlMessageProvider>
        </Provider>
    </SettingsProvider>
);

DrumallReduxApp.propTypes = {
    initialMessages: PropTypes.object.isRequired
};

export default DrumallReduxApp;
